import { useRef } from 'react';
import type { ValueType } from 'react-select';

import type { Billable, IEvent, IEventBilling, ReportModel } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';
import type { TValidateGrouped } from '@feathr/rachis';

interface IProjectFormErrors extends TValidateGrouped {
  name?: string;
}

export interface IProjectFormReturn {
  event: ReportModel<IEvent>;
  billing: IEventBilling;
  billable: Billable | undefined;
  validationErrors: IProjectFormErrors;
  handleBillingChange: (selectedBillable?: ValueType<Billable>) => void;
  validateEvent: (event: ReportModel<IEvent>) => IProjectFormErrors;
}

/**
 * Custom hook that manages shared project/event form functionality
 * Used by both EventAdd.tsx and CreateFirstProjectModal.tsx
 */
export function useProjectForm(): IProjectFormReturn {
  const { Billables, Events } = useStore();

  const eventRef = useRef<ReportModel<IEvent> | undefined>(undefined);
  // Initializing this way to avoid re-creating the ref contents - see https://react.dev/reference/react/useRef#avoiding-recreating-the-ref-contents
  if (eventRef.current === undefined) {
    eventRef.current = Events.create();
  }
  const event = eventRef.current;

  const billing = event.get('billing', {} as IEventBilling);
  const billable = billing.billable_id ? Billables.get(billing.billable_id) : undefined;

  function handleBillingChange(selectedBillable?: ValueType<Billable>): void {
    if (!selectedBillable || Array.isArray(selectedBillable)) {
      return;
    }
    event.set({
      billing: {
        ...billing,
        billable_id: (selectedBillable as Billable).id,
      } as IEventBilling,
    });
  }

  function validateEvent(event: ReportModel<IEvent>): IProjectFormErrors {
    return event.validate(['name'], false, 'grouped').errors;
  }

  const validationErrors = validateEvent(event);

  return {
    event,
    billing,
    billable,
    validationErrors,
    handleBillingChange,
    validateEvent,
  };
}
